import {
  Button,
  Chip,
  Collapse,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useCallback, useRef, useState } from 'react';
import {
  Cancel,
  SearchOutlined,
  ChevronRight,
  KeyboardArrowDown,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useQueryParams, serializeArrayParam } from 'hooks/useQueryParams';
import { MapSearchURLParams } from 'hooks/useMapSearch';
import { makeStyles } from 'tss-react/mui';
import { useAuthState } from 'state/useAuthState';
import { ExtendedKeyboardEvent } from 'mousetrap';
import { useSearchBarCommandPaletteActions } from 'hooks/commandPaletteActions';
import { useLayersDrawerState } from 'features/mapping/hooks/useLayersDrawerState';
import { StickyGuard } from 'components/StickyGuard';
import { useSelectedGeoEventId } from 'features/geoEvents/hooks/useSelectedGeoEventId';
import { getDenseResponsiveFontSize } from 'theme/watchdutyTheme.utils';
import { useIncidentFiltersState } from '../../../state/useIncidentFiltersState';
import { useSearchDrawerState } from '../../../state/useSearchDrawerState';
import { FilterSelector } from './FilterSelector';
import { useSyncFiltersWithUrlParams } from './useSyncFiltersWithUrlParams';
import { useShowAdvancedSearch } from './useShowAdvancedSearch';

type SearchBarProps = {
  onCancel?: () => void;
  scrollbarRef?: React.RefObject<HTMLDivElement | Element>;
  onFocus?: () => void;
  onBeforeFocusDrawerElement?: () => void;
  isSheetOpen?: boolean;
};

const useStyles = makeStyles()((theme) => ({
  sticky: {
    background: theme.palette.background.default,
    zIndex: theme.zIndex.drawer + 1,
    '&:after': {
      content: '""',
      position: 'absolute',
      width: 'calc(100% - 8px)',
      bottom: 0,
      left: 4,
      zIndex: -1,
      boxShadow: '0px 0px 4px 4px rgba(0, 0, 0, 0.25)',
    },
  },
}));

export const SearchBar = (props: SearchBarProps): JSX.Element => {
  const {
    onCancel,
    scrollbarRef,
    onFocus,
    onBeforeFocusDrawerElement,
    isSheetOpen = false,
  } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const isLargeMediaQuery = useMediaQuery(theme.breakpoints.up('tablet'));
  const [params, setParams] = useQueryParams<MapSearchURLParams>();
  const { search: searchQuery = '', searchType } = params;
  const [isFocused, setIsFocused] = useState(false);
  const [advancedSearchExpanded, setAdvancedSearchExpanded] = useState(false);

  const {
    showMembershipProFeatures,
    permissions: { isInternalUser },
  } = useAuthState();
  const isHistoricalSearch =
    searchType === 'inactive' && showMembershipProFeatures;
  const { classes } = useStyles();
  const selectedGeoEventId = useSelectedGeoEventId();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { open: isSearchDrawerOpen, toggleOpen: toggleSearchDrawer } =
    useSearchDrawerState();
  const { open: isLayersDrawerOpen } = useLayersDrawerState();
  const { filters, filtersCount } = useIncidentFiltersState();

  useSyncFiltersWithUrlParams();

  // The search drawer is only closed using "Escape" when it was also opened
  // using a keyboard shortcut. Otherwise, it will blur the search input field
  // but leave the drawer open.
  const [wasOpenedUsingKeyboardShortcut, setWasOpenedUsingKeyboardShortcut] =
    useState(false);

  const handleFocusKeyboardShortcut = useCallback(
    (event?: ExtendedKeyboardEvent): void => {
      event?.preventDefault();

      // Open search drawer first if it's not already open
      if (!isSearchDrawerOpen) {
        setWasOpenedUsingKeyboardShortcut(true);
        toggleSearchDrawer();
        setTimeout(
          () => searchInputRef.current?.focus(),
          theme.transitions.duration.short,
        );

        return;
      }

      searchInputRef.current?.focus();
    },
    [isSearchDrawerOpen, theme, toggleSearchDrawer],
  );

  const handleSearchBarKeyup = (event: React.KeyboardEvent): void => {
    if (event.key !== 'Escape' || !isSearchDrawerOpen) {
      return;
    }

    searchInputRef.current?.blur();
    if (wasOpenedUsingKeyboardShortcut) {
      setWasOpenedUsingKeyboardShortcut(false);
      toggleSearchDrawer();
    }
  };

  useSearchBarCommandPaletteActions(handleFocusKeyboardShortcut);

  const setSearchQuery = (newValue: MapSearchURLParams['search']): void => {
    setParams({
      search: newValue,
      searchType,
      ownership: filters.ownership,
      urgency: serializeArrayParam(filters.urgency),
      metadata: serializeArrayParam(filters.metadata),
    });
  };

  const handleClear = (): void => {
    setSearchQuery(null);
  };

  const handleClearDrawer = (): void => {
    setSearchQuery(null);
    if (onCancel) onCancel();
  };

  const toggleHistoricalFilter = (): void => {
    if (isHistoricalSearch) {
      setParams({
        ...params,
        searchType: null,
      });
    } else {
      setParams({
        ...params,
        searchType: 'inactive',
      });
    }
  };

  // Fires on pointer-down so the parent can lock before the blur triggers
  // the keyboard hide.
  const handlePointerDown = (): void => {
    if (onBeforeFocusDrawerElement) onBeforeFocusDrawerElement();
  };

  const showAdvancedSearch = useShowAdvancedSearch(isSheetOpen);

  return (
    <StickyGuard
      scrollElement={scrollbarRef?.current as HTMLElement}
      stickyClassName={classes.sticky}
    >
      <Stack
        sx={{
          // Explicit background to make sticky scroll shadow work
          backgroundColor: 'background.paper',
          gap: 2,
          pb: 2,
        }}
      >
        <Stack
          direction="row"
          sx={{
            marginX: 2,
            marginTop: isLargeMediaQuery ? 2 : 0,
          }}
        >
          <TextField
            fullWidth
            placeholder={t('searchBar.geoEventsPlaceholder')}
            variant="filled"
            className="watchduty-filled-nolabel"
            value={searchQuery || ''}
            onKeyUp={handleSearchBarKeyup}
            onChange={(event) => setSearchQuery(event.target.value)}
            onFocus={() => {
              if (onFocus) onFocus();
              setIsFocused(true);
            }}
            onBlur={() => {
              // keep focus for a moment in case we are unfocusing for a button that will disappear
              setTimeout(() => setIsFocused(false), 100);
            }}
            // Workaround: Prevent search drawer from opening to avoid interference with
            // end-to-end tests when selecting an incident or opening the layers drawer.
            // Only needed for iOS (simulator and SauceLabs), but doesn't affect any functionality.
            disabled={Boolean(selectedGeoEventId) || isLayersDrawerOpen}
            slotProps={{
              input: {
                sx: {
                  height: 42,
                  alignItems: 'center',
                },
                inputProps: {
                  ref: searchInputRef,
                },
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlined
                      fontSize="medium"
                      sx={{ color: 'text.secondary' }}
                    />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleClear}
                      onPointerDown={handlePointerDown}
                      aria-label={t('searchBar.clearSearch')}
                      sx={{ marginRight: -1 }}
                    >
                      <Cancel fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />

          {!isLargeMediaQuery && isFocused && (
            <Button
              size="small"
              variant="text"
              sx={{
                color: 'accent.main',
                marginRight: -1,
                fontSize: getDenseResponsiveFontSize('0.8125rem'),
              }}
              onClick={handleClearDrawer}
            >
              {t('common.cancel')}
            </Button>
          )}
        </Stack>

        {showAdvancedSearch && (
          <Stack paddingX={2}>
            <Button
              disableRipple
              variant="text"
              color="inherit"
              startIcon={
                advancedSearchExpanded ? (
                  <KeyboardArrowDown />
                ) : (
                  <ChevronRight />
                )
              }
              onClick={() => setAdvancedSearchExpanded((prev) => !prev)}
              onPointerDown={handlePointerDown}
              sx={{
                justifyContent: 'flex-start',
                px: 0,
                py: 0.25,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" fontWeight="500">
                  {t('searchBar.advancedSearch')}
                </Typography>
                {!advancedSearchExpanded && filtersCount > 0 && (
                  <Chip
                    label={filtersCount}
                    variant="watchduty"
                    color="error"
                  />
                )}
              </Stack>
            </Button>

            <Collapse
              in={advancedSearchExpanded}
              onExited={() => {
                // Force StickyGuard to recalculate holder height after collapse animation
                window.dispatchEvent(new Event('resize'));
              }}
            >
              <Stack spacing={2} paddingTop={2}>
                {isInternalUser && (
                  <FilterSelector
                    onBeforeFocusDrawerElement={handlePointerDown}
                  />
                )}
                <Stack
                  direction="row"
                  sx={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginX: 2,
                    marginY: 1,
                  }}
                >
                  <Typography>{t('searchBar.historicalIncidents')}</Typography>
                  <Switch
                    onPointerDown={handlePointerDown}
                    checked={searchType === 'inactive'}
                    onChange={toggleHistoricalFilter}
                    slotProps={{
                      input: {
                        'aria-label': t('searchBar.historicalIncidents'),
                      },
                    }}
                  />
                </Stack>
              </Stack>
            </Collapse>
          </Stack>
        )}
      </Stack>
    </StickyGuard>
  );
};
